import type * as Types from './types';

import {
  useQuery,
  type QueryClient,
  useMutation,
  type UseQueryOptions,
  type FetchQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type { DXError } from '@dx-ui/types-graphql';

export const FeatureTogglesDocument = {
  operationName: 'featureToggles',
  operationString: `query featureToggles($names: [String!]!) {
  featureToggles(names: $names) {
    enabled
    name
  }
}`,
  originalOpName: 'featureToggles',
};

export function useFeatureTogglesQuery<TData = Types.FeatureTogglesQuery, TError = DXError>(
  variables: Types.FeatureTogglesQueryVariables,
  options?: Omit<UseQueryOptions<Types.FeatureTogglesQuery, TError, TData>, 'queryKey'>
) {
  return useQuery<Types.FeatureTogglesQuery, TError, TData>({
    queryKey: [FeatureTogglesDocument, variables],
    ...options,
  });
}

export function serverSideFeatureTogglesQuery(
  queryClient: QueryClient,
  variables: Types.FeatureTogglesQueryVariables,
  options?: FetchQueryOptions<Types.FeatureTogglesQuery>
) {
  return queryClient.fetchQuery<Types.FeatureTogglesQuery>({
    queryKey: [FeatureTogglesDocument, variables],
    ...options,
  });
}

export const Brand_CountriesDocument = {
  operationName: 'brand_countries',
  operationString: `query brand_countries($after: String, $containsHotels: Boolean, $first: Int, $language: String!, $countryFilter: CountryFilterInput, $regionFilter: CountryStateFilterInput, $cityFilter: CountryCityFilterInput, $getStates: Boolean = false, $getCities: Boolean = false, $getMarketing: Boolean = false, $getCCSMarketing: Boolean = false) {
  countries(
    after: $after
    containsHotels: $containsHotels
    filter: $countryFilter
    first: $first
    language: $language
    sort: {by: name}
  ) {
    code
    callingCode
    dataSubjectRights
    marketingConsent @include(if: $getMarketing)
    marketingOptIn @include(if: $getMarketing)
    marketingOptInForHGV @include(if: $getMarketing)
    marketingOptin @include(if: $getCCSMarketing) {
      dataSubjectRights
      marketingOptinRules {
        category
        optIns {
          autoOptIn
          _id
          customerTextExternal
          name
          subscriptionCodes
          termsAndConditionsBook
          termsAndConditionsJoin
        }
      }
    }
    name: displayName
    states(filter: $regionFilter) @include(if: $getStates) {
      name
      code
      personalDataRequest
      value: code
      cities(filter: $cityFilter, sort: {by: name, order: asc}) @include(if: $getCities) {
        name
        value: name
        brandCodes
      }
    }
    cities(filter: $cityFilter, onlyIfNoStates: true, sort: {by: name, order: asc}) @include(if: $getCities) {
      name
      value: name
      brandCodes
    }
  }
}`,
  originalOpName: 'brand_countries',
};

export function useBrand_CountriesQuery<TData = Types.Brand_CountriesQuery, TError = DXError>(
  variables: Types.Brand_CountriesQueryVariables,
  options?: Omit<UseQueryOptions<Types.Brand_CountriesQuery, TError, TData>, 'queryKey'>
) {
  return useQuery<Types.Brand_CountriesQuery, TError, TData>({
    queryKey: [Brand_CountriesDocument, variables],
    ...options,
  });
}

export function serverSideBrand_CountriesQuery(
  queryClient: QueryClient,
  variables: Types.Brand_CountriesQueryVariables,
  options?: FetchQueryOptions<Types.Brand_CountriesQuery>
) {
  return queryClient.fetchQuery<Types.Brand_CountriesQuery>({
    queryKey: [Brand_CountriesDocument, variables],
    ...options,
  });
}

export const CreateGuestDocument = {
  operationName: 'createGuest',
  operationString: `mutation createGuest($input: EnrollInput!, $language: String!, $recaptchaInput: RecaptchaInput, $autoLogin: Boolean) {
  createGuest(
    input: $input
    language: $language
    recaptchaInput: $recaptchaInput
    autoLogin: $autoLogin
  ) {
    data {
      guestId
      hhonorsNumber
      accessToken
      autoLoginStatus
    }
    error {
      code
      context
      message
      notifications {
        code
        fields
        message
      }
    }
  }
}`,
  originalOpName: 'createGuest',
};

export function useCreateGuestMutation<TError = DXError, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateGuestMutation,
    TError,
    [typeof CreateGuestDocument, Types.CreateGuestMutationVariables],
    TContext
  >
) {
  return useMutation<
    Types.CreateGuestMutation,
    TError,
    [typeof CreateGuestDocument, Types.CreateGuestMutationVariables],
    TContext
  >({
    mutationKey: ['createGuest'],
    ...options,
  });
}

export const Languages_DefaultCountryCodeDocument = {
  operationName: 'languages_defaultCountryCode',
  operationString: `query languages_defaultCountryCode($language: String!) {
  languages(language: $language, filter: {languageCodeOHW: $language}) {
    defaultCountryCode
  }
}`,
  originalOpName: 'languages_defaultCountryCode',
};

export function useLanguages_DefaultCountryCodeQuery<
  TData = Types.Languages_DefaultCountryCodeQuery,
  TError = DXError,
>(
  variables: Types.Languages_DefaultCountryCodeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.Languages_DefaultCountryCodeQuery, TError, TData>,
    'queryKey'
  >
) {
  return useQuery<Types.Languages_DefaultCountryCodeQuery, TError, TData>({
    queryKey: [Languages_DefaultCountryCodeDocument, variables],
    ...options,
  });
}

export function serverSideLanguages_DefaultCountryCodeQuery(
  queryClient: QueryClient,
  variables: Types.Languages_DefaultCountryCodeQueryVariables,
  options?: FetchQueryOptions<Types.Languages_DefaultCountryCodeQuery>
) {
  return queryClient.fetchQuery<Types.Languages_DefaultCountryCodeQuery>({
    queryKey: [Languages_DefaultCountryCodeDocument, variables],
    ...options,
  });
}

export const TermsDocument = {
  operationName: 'terms',
  operationString: `query terms($language: String!, $countryCode: String!) {
  terms(language: $language, countryCode: $countryCode) {
    _id
    description
    document
    termId
    version
  }
}`,
  originalOpName: 'terms',
};

export function useTermsQuery<TData = Types.TermsQuery, TError = DXError>(
  variables: Types.TermsQueryVariables,
  options?: Omit<UseQueryOptions<Types.TermsQuery, TError, TData>, 'queryKey'>
) {
  return useQuery<Types.TermsQuery, TError, TData>({
    queryKey: [TermsDocument, variables],
    ...options,
  });
}

export function serverSideTermsQuery(
  queryClient: QueryClient,
  variables: Types.TermsQueryVariables,
  options?: FetchQueryOptions<Types.TermsQuery>
) {
  return queryClient.fetchQuery<Types.TermsQuery>({
    queryKey: [TermsDocument, variables],
    ...options,
  });
}
